import { action, Action, computed, Computed, thunk } from 'easy-peasy';
import { API } from '@xbto/api-client';
import { ApiThunk } from './types';
import { runApi } from '../utils';
import { EnrichedApiKey, GlobalAppSettings } from '../types';
import {
  cryptoAddressesModel,
  CryptoAddressesModel,
} from './crypto-addresses-store';
import { BaseModel, createBaseModel } from './base-store';

type OperationResult = {
  operationId: string;
  status: API.QuorumOperationStatus;
};

export type RelationshipManager = {
  name: string;
  email: string;
  phone: string;
};

export interface SettingsModel extends BaseModel {
  // state
  tag: string | null;
  bankAccounts: API.BankAccount[] | null;
  bankAccount: API.BankAccount | null;
  bankAccountRequirements: API.BankAccountRequirementsOutput | null;
  notificationsSettings: API.NotificationsSettings | null;
  globalAppSettings: GlobalAppSettings | null;
  apiKeys: EnrichedApiKey[] | null;
  newlyCreatedApiKey: API.CreateApiKeyResponse | null;
  cryptoAddresses: CryptoAddressesModel;
  // computed
  hasNonVerifiedBankAccounts: Computed<SettingsModel, boolean>;
  relationshipManager: Computed<SettingsModel, RelationshipManager | null>;

  // actions
  setBankAccounts: Action<SettingsModel, API.BankAccount[] | null>;
  setBankAccount: Action<SettingsModel, API.BankAccount | null>;
  setBankAccountRequirements: Action<
    SettingsModel,
    API.BankAccountRequirementsOutput | null
  >;
  setTag: Action<SettingsModel, string | null>;
  setNotificationsSettings: Action<SettingsModel, API.NotificationsSettings>;
  setGlobalAppSettings: Action<SettingsModel, GlobalAppSettings | null>;
  setApiKeys: Action<SettingsModel, EnrichedApiKey[]>;
  setNewlyCreatedApiKey: Action<SettingsModel, API.CreateApiKeyResponse>;
  // thunk
  getTag: ApiThunk<SettingsModel, void, API.GetTagResponse>;
  loadNotificationsSettings: ApiThunk<SettingsModel, void>;
  updateNotificationsSettings: ApiThunk<
    SettingsModel,
    API.NotificationsSettings
  >;
  getBankAccounts: ApiThunk<SettingsModel, void, API.BankAccount[]>;
  getBankAccount: ApiThunk<
    SettingsModel,
    API.GetBankAccountRequest,
    API.BankAccount
  >;
  getBankAccountRequirements: ApiThunk<
    SettingsModel,
    API.BankAccountRequirementsInput,
    API.BankAccountRequirementsOutput
  >;
  getAccountSettings: ApiThunk<
    SettingsModel,
    void,
    API.AccountSettingsResponse
  >;
  createBankAccount: ApiThunk<
    SettingsModel,
    API.CreateBankAccountRequest,
    API.BankAccount
  >;
  createBankAccountOperation: ApiThunk<
    SettingsModel,
    API.CreateBankAccountRequest,
    OperationResult
  >;
  verifyBankAccount: ApiThunk<SettingsModel, API.VerifyBankAccountRequest>;
  resendBankAccountVerificationCode: ApiThunk<
    SettingsModel,
    API.ResendBankAccountVerificationCodeRequest
  >;
  deleteBankAccount: ApiThunk<SettingsModel, API.DeleteBankAccountRequest>;
  deleteBankAccountOperation: ApiThunk<
    SettingsModel,
    API.DeleteBankAccountRequest,
    OperationResult
  >;
  getApiKeys: ApiThunk<SettingsModel, void>;
  createApiKey: ApiThunk<
    SettingsModel,
    API.CreateApiKeyRequest & { accountId: string },
    API.CreateApiKeyResponse
  >;
  deleteApiKey: ApiThunk<SettingsModel, API.DeleteApiKeyRequest>;
  getFees: ApiThunk<
    SettingsModel,
    API.DownloadOrganizationDocumentRequest,
    API.GetOrganizationDocumentResponse
  >;
}

export const settingsModel: SettingsModel = {
  ...createBaseModel(),

  tag: null,
  bankAccounts: null,
  bankAccount: null,
  bankAccountRequirements: null,
  notificationsSettings: null,
  globalAppSettings: null,
  apiKeys: null,
  newlyCreatedApiKey: null,
  cryptoAddresses: cryptoAddressesModel,
  // computed
  hasNonVerifiedBankAccounts: computed(
    [s => s.bankAccounts ?? []],
    bankAccounts =>
      bankAccounts.some(
        ({ status }) => status === API.CustomerBankAccountStatus.Verifying
      )
  ),
  relationshipManager: computed(
    [
      s => s.globalAppSettings?.relationshipManagerName,
      s => s.globalAppSettings?.relationshipManagerEmail,
      s => s.globalAppSettings?.relationshipManagerPhoneNumber,
    ],
    (name, email, phone) => {
      if (!name || !email || !phone) {
        return null;
      }

      return {
        name: name,
        email: email,
        phone: phone,
      };
    }
  ),
  // actions
  setBankAccounts: action((state, payload) => {
    state.bankAccounts = payload;
  }),
  setBankAccount: action((state, payload) => {
    state.bankAccount = payload;
  }),
  setBankAccountRequirements: action((state, payload) => {
    state.bankAccountRequirements = payload;
  }),
  setTag: action((state, payload) => {
    state.tag = payload;
  }),
  setNotificationsSettings: action((state, payload) => {
    state.notificationsSettings = payload;
  }),
  setGlobalAppSettings: action((state, payload) => {
    state.globalAppSettings = payload;
  }),
  setApiKeys: action((state, payload) => {
    state.apiKeys = payload;
  }),
  setNewlyCreatedApiKey: action((state, payload) => {
    state.newlyCreatedApiKey = payload;
  }),
  // thunk
  getTag: thunk(async (actions, _void, helpers) => {
    return runApi(
      actions,
      helpers,
      () => {
        return helpers.injections.apiClient.getTag();
      },
      result => {
        actions.setTag(result.tag);
      }
    );
  }),
  getBankAccounts: thunk(async (actions, _void, helpers) => {
    return runApi(
      actions,
      helpers,
      () => {
        return helpers.injections.apiClient.getBankAccounts(undefined);
      },
      result => {
        actions.setBankAccounts(result);
      }
    );
  }),
  getBankAccount: thunk(async (actions, req, helpers) => {
    return runApi(
      actions,
      helpers,
      () => {
        return helpers.injections.apiClient.getBankAccount(req);
      },
      result => {
        actions.setBankAccount(result);
      }
    );
  }),
  getBankAccountRequirements: thunk(async (actions, req, helpers) => {
    return runApi(
      actions,
      helpers,
      () => {
        return helpers.injections.apiClient.getBankAccountRequirements(req);
      },
      result => {
        actions.setBankAccountRequirements(result);
      }
    );
  }),
  getAccountSettings: thunk(async (actions, _void, helpers) => {
    return runApi(
      actions,
      helpers,
      () => {
        return helpers.injections.apiClient.getAccountSettings();
      },

      result => {
        const isUserAdminOrFundManager =
          helpers.getStoreState().user.clientUserType === 'admin' ||
          helpers.getStoreState().user.clientUserType === 'fund-manager';

        actions.setGlobalAppSettings({
          ...result,
          relationshipManagerEmailLink: `mailto:${result.relationshipManagerEmail}`,
          tradingDeskContactEmail: 'tradingdesk@stablehouse.com',
          tradingDeskContactEmailLink: 'mailto:tradingdesk@stablehouse.com',
          tradingDeskContactName: 'George Mandres',
          tradingDeskContactPhone: '+1 646 934 6723',
          tradingDeskContactPhoneLink: 'tel:+16469346723',
          showRelationshipManagerInfo:
            !!result.relationshipManagerName ||
            !!result.relationshipManagerEmail,
          showTradingDeskInfo:
            !!result.relationshipManagerName ||
            !!result.relationshipManagerEmail,
          showFundsExperience: !isUserAdminOrFundManager,
        });
      }
    );
  }),
  createBankAccount: thunk(async (actions, req, helpers) => {
    return runApi(actions, helpers, () => {
      return helpers.injections.apiClient.createBankAccount(req);
    });
  }),
  createBankAccountOperation: thunk(async (actions, req, helpers) => {
    return runApi(actions, helpers, async () => {
      const res =
        await helpers.injections.apiClient.createBankAccountOperation(req);

      // Accommodate `runApi` TS
      return {
        ...res,
        status: res.isSuccessful ? 200 : 400,
        result: {
          operationId: res.operationId,
          status: res.status,
        },
      };
    });
  }),
  verifyBankAccount: thunk(async (actions, req, helpers) => {
    return runApi(actions, helpers, () => {
      return helpers.injections.apiClient.verifyBankAccount(req);
    });
  }),
  resendBankAccountVerificationCode: thunk(async (actions, req, helpers) => {
    return runApi(actions, helpers, () => {
      return helpers.injections.apiClient.resendBankAccountVerificationCode(
        req
      );
    });
  }),
  deleteBankAccount: thunk(async (actions, req, helpers) => {
    return runApi(actions, helpers, () => {
      return helpers.injections.apiClient.deleteBankAccount(req);
    });
  }),
  deleteBankAccountOperation: thunk((actions, req, helpers) => {
    return runApi(actions, helpers, async () => {
      const res =
        await helpers.injections.apiClient.deleteBankAccountOperation(req);

      // Accommodate `runApi` TS
      return {
        ...res,
        status: res.isSuccessful ? 200 : 400,
        result: {
          operationId: res.operationId,
          status: res.status,
        },
      };
    });
  }),
  loadNotificationsSettings: thunk(async (actions, _void, helpers) => {
    return runApi(
      actions,
      helpers,
      () => {
        return helpers.injections.apiClient.getNotificationsSettings();
      },
      result => {
        actions.setNotificationsSettings(result);
      }
    );
  }),
  updateNotificationsSettings: thunk(async (actions, req, helpers) => {
    return runApi(actions, helpers, async () => {
      const res =
        await helpers.injections.apiClient.setNotificationsSettings(req);

      if (res.isSuccessful) {
        // TODO(Benoit): Make this a side-effect of `updateNotificationsSettings` action
        await actions.loadNotificationsSettings();
      }

      return res;
    });
  }),
  getApiKeys: thunk(async (actions, _void, helpers) => {
    const storeState = helpers.getStoreState();

    return runApi(
      actions,
      helpers,
      () => {
        return helpers.injections.apiClient.getApiKeys();
      },
      async result => {
        const accounts = storeState.portfolio.accounts;
        const enrichedKeys = result.map(key => {
          const account = accounts.find(
            acc => acc.account?.accountId === key.accountId
          );
          return {
            ...key,
            accountId: account?.account?.accountId || null,
            accountNumber: account?.account?.accountName || null,
            accountLabel: account?.account?.label || null,
          };
        });

        actions.setApiKeys(enrichedKeys);
      }
    );
  }),
  createApiKey: thunk(async (actions, { accountId, ...req }, helpers) => {
    return runApi(
      actions,
      helpers,
      () => {
        return helpers.injections.apiClient.createApiKey(req);
      },
      async result => {
        actions.setNewlyCreatedApiKey(result);
      },
      { 'x-account-id': accountId }
    );
  }),
  deleteApiKey: thunk(async (actions, req, helpers) => {
    return runApi(actions, helpers, async () => {
      await helpers.injections.apiClient.deleteApiKey(req);
      return { isSuccessful: true };
    });
  }),
  getFees: thunk((actions, payload, helpers) => {
    return runApi(
      actions,
      helpers,
      () => {
        return helpers.injections.apiClient.downloadDocumentType(payload);
      },
      undefined,
      {
        'x-account-id': helpers.getStoreState().user.defaultAccountId,
      }
    );
  }),
};
